import { ref } from 'vue';
import { notificationsService } from '@/service/nexus';

const priorityConfig = {
    urgent: { icon: 'pi-bell', color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-900/20' },
    high: { icon: 'pi-exclamation-triangle', color: 'text-orange-500', bg: 'bg-orange-50 dark:bg-orange-900/20' },
    medium: { icon: 'pi-info-circle', color: 'text-surface-500', bg: 'bg-surface-100 dark:bg-surface-800' },
    low: { icon: 'pi-info-circle', color: 'text-surface-500', bg: 'bg-surface-100 dark:bg-surface-800' }
};

const priorityOptions = [
    { value: 'all', label: 'All priorities' },
    { value: 'medium_and_above', label: 'Medium and above' },
    { value: 'high_and_above', label: 'High and urgent' },
    { value: 'urgent', label: 'Urgent only' }
];

function formatTimeAgo(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
}

function formatLienDate(value) {
    if (!value) return null;
    // Date-only strings ("YYYY-MM-DD") are parsed by `new Date()` as UTC
    // midnight, which renders as the prior day in any locale west of UTC.
    // Parse the components ourselves so the rendered date matches the
    // calendar date the backend sent.
    if (typeof value === 'string') {
        const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (dateOnlyMatch) {
            const [, y, m, d] = dateOnlyMatch;
            const local = new Date(Number(y), Number(m) - 1, Number(d));
            if (!isNaN(local.getTime())) return local.toLocaleDateString();
        }
    }
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString();
}

function formatLienAmount(value) {
    if (value === null || value === undefined || value === '') return null;
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (Number.isNaN(num)) return String(value);
    try {
        return num.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
    } catch {
        return `$${num.toFixed(2)}`;
    }
}

function buildLienAutoAppliedSummary(data) {
    const parts = [];
    const amount = formatLienAmount(data.lien_amount);
    if (amount) parts.push(`Amount ${amount}`);
    if (data.lien_status) parts.push(`Status ${data.lien_status}`);
    if (data.lien_servicer) parts.push(`Servicer ${data.lien_servicer}`);
    if (data.insurance_carrier) parts.push(`Carrier ${data.insurance_carrier}`);
    const start = formatLienDate(data.service_date_start);
    const end = formatLienDate(data.service_date_end);
    if (start && end) parts.push(`Service ${start} – ${end}`);
    else if (start) parts.push(`Service from ${start}`);
    else if (end) parts.push(`Service to ${end}`);
    return parts.join(' • ');
}

// Manual-match follow-up (F019): counts per attachment outcome → one line,
// zero groups omitted. "Matched existing" mirrors the nexus message
// (adopted_existing only); reviewer-resolved duplicates get their own group.
const FOLLOWUP_GROUPS = [
    { keys: ['ingested_new'], label: () => 'ingested' },
    { keys: ['adopted_existing'], label: () => 'matched existing' },
    { keys: ['excluded_inline_image'], label: () => 'excluded' },
    { keys: ['needs_review_possible_duplicate'], label: () => 'need decision' },
    { keys: ['needs_review_duplicate_resolved'], label: () => 'resolved' },
    { keys: ['error'], label: (n) => (n === 1 ? 'error' : 'errors') }
];

function buildFollowupSummary(counts) {
    if (!counts || typeof counts !== 'object') return '';
    return FOLLOWUP_GROUPS.map(({ keys, label }) => {
        const n = keys.reduce((sum, key) => sum + (Number.isFinite(counts[key]) ? counts[key] : 0), 0);
        return n > 0 ? `${n} ${label(n)}` : null;
    })
        .filter(Boolean)
        .join(' · ');
}

function followupLink(data) {
    // A route object keeps message ids with "+" or "/" intact in the query.
    if (data.message_id) return { path: '/mail/messages', query: { message: String(data.message_id) } };
    return data.notification_link || null;
}

function parseNotification(notification) {
    const isBatch = notification.event_type === 'batch_notification';
    const isLienAutoApplied = notification.event_type === 'lien_auto_applied';
    const isManualMatchFollowup = notification.event_type === 'manual_match_followup';
    const data = notification.data || {};

    let config;
    if (isBatch) {
        config = { icon: 'pi-inbox', color: 'text-indigo-500', bg: 'bg-indigo-50 dark:bg-indigo-900/20' };
    } else if (isLienAutoApplied) {
        config = { icon: 'pi-check-circle', color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-900/20' };
    } else if (isManualMatchFollowup) {
        config = { ...(priorityConfig[notification.priority] || priorityConfig.medium), icon: 'pi-paperclip' };
    } else {
        config = priorityConfig[notification.priority] || priorityConfig.medium;
    }

    let description = notification.message;
    if (isBatch && data.batch_count) {
        description = `${data.batch_count} notifications bundled together. Click to view details.`;
    } else if (isLienAutoApplied) {
        const summary = buildLienAutoAppliedSummary(data);
        if (summary) description = summary;
    } else if (isManualMatchFollowup) {
        const summary = buildFollowupSummary(data.counts);
        if (summary) description = summary;
    }

    return {
        id: notification.id,
        title: notification.title,
        description,
        time: formatTimeAgo(notification.created_at),
        read: notification.delivery_status === 'read',
        priority: notification.priority,
        icon: 'pi ' + config.icon,
        iconColor: config.color,
        iconBg: config.bg,
        caseId: notification.case_id,
        caseNumber: data.case_number,
        caseName: data.case_name,
        documentId: notification.document_id,
        actionId: notification.action_id,
        eventType: notification.event_type,
        isBatch: isBatch,
        batchCount: data.batch_count,
        documentGeneratedName: data.document_generated_name,
        documentCategory: data.document_category,
        documentSummary: data.document_summary,
        inboundId: data.inbound_id,
        inboundSource: data.inbound_source,
        caseExternalLink: data.case_external_link,
        // Lien auto-applied fields. neosLienLink prefers the lien-tab deep link,
        // falling back to the case URL so the "Edit in Neos" link always works.
        isLienAutoApplied,
        neosLienLink: isLienAutoApplied ? data.neos_lien_link || data.case_external_link || null : null,
        lienAmount: isLienAutoApplied ? formatLienAmount(data.lien_amount) : null,
        lienStatus: isLienAutoApplied ? data.lien_status || null : null,
        lienServicer: isLienAutoApplied ? data.lien_servicer || null : null,
        lienInsuranceCarrier: isLienAutoApplied ? data.insurance_carrier || null : null,
        lienServiceDateStart: isLienAutoApplied ? formatLienDate(data.service_date_start) : null,
        lienServiceDateEnd: isLienAutoApplied ? formatLienDate(data.service_date_end) : null,
        // Manual-match follow-up fields (F019).
        isManualMatchFollowup,
        counts: isManualMatchFollowup ? data.counts || {} : null,
        messageId: isManualMatchFollowup ? (data.message_id ?? null) : null,
        link: isManualMatchFollowup ? followupLink(data) : null
    };
}

// Shared state across all components (singleton)
const unreadCount = ref(0);
const unreadStats = ref({ total: 0, urgent: 0, high: 0, other: 0 });
const sourceCounts = ref({ fax: 0, email: 0, text: 0, manual: 0 });

export function useNotifications() {
    // Per-instance state (each component gets its own list)
    const notifications = ref([]);
    const loading = ref(false);
    const loadingMore = ref(false);
    const currentPage = ref(1);
    const totalCount = ref(0);
    const hasMore = ref(false);

    async function loadNotifications({ page = 1, append = false, status, priority = 'medium_and_above', source, case_id } = {}) {
        try {
            if (!append) {
                loading.value = true;
            } else {
                loadingMore.value = true;
            }

            const params = { page, page_size: 20, priority };
            if (status && status !== 'all') params.status = status;
            if (source) params.source = source;
            if (case_id) params.case_id = case_id;

            const response = await notificationsService.getNotifications(params);
            const parsed = response.data.results.map(parseNotification);

            if (append) {
                notifications.value = [...notifications.value, ...parsed];
            } else {
                notifications.value = parsed;
            }

            currentPage.value = page;
            totalCount.value = response.data.count;
            hasMore.value = response.data.next !== null;
        } catch (error) {
            console.error('Failed to load notifications:', error);
        } finally {
            loading.value = false;
            loadingMore.value = false;
        }
    }

    async function loadUnreadCount() {
        try {
            const response = await notificationsService.getUnreadNotifications();
            const items = Array.isArray(response.data) ? response.data : response.data.results || [];
            const total = Array.isArray(response.data) ? items.length : response.data.count || items.length;
            unreadCount.value = total;
            unreadStats.value = {
                total,
                urgent: items.filter((n) => n.priority === 'urgent').length,
                high: items.filter((n) => n.priority === 'high').length,
                other: items.filter((n) => n.priority === 'medium' || n.priority === 'low').length
            };
        } catch (error) {
            console.error('Failed to load unread count:', error);
        }
    }

    async function loadSourceCounts({ priority = 'medium_and_above' } = {}) {
        try {
            const response = await notificationsService.getSourceCounts({ priority });
            sourceCounts.value = response.data;
        } catch (error) {
            console.error('Failed to load source counts:', error);
        }
    }

    async function markAsRead(notificationId) {
        try {
            await notificationsService.markAsRead(notificationId);
            const notification = notifications.value.find((n) => n.id === notificationId);
            if (notification) {
                notification.read = true;
            }
            unreadCount.value = Math.max(0, unreadCount.value - 1);
        } catch (error) {
            console.error('Failed to mark notification as read:', error);
        }
    }

    async function markAllAsRead({ source } = {}) {
        try {
            await notificationsService.markAllAsRead(source ? { source } : {});
            notifications.value = notifications.value.map((n) => ({ ...n, read: true }));
            unreadCount.value = 0;
        } catch (error) {
            console.error('Failed to mark all notifications as read:', error);
        }
    }

    return {
        notifications,
        loading,
        loadingMore,
        unreadCount,
        unreadStats,
        currentPage,
        totalCount,
        hasMore,
        sourceCounts,
        loadNotifications,
        loadUnreadCount,
        loadSourceCounts,
        markAsRead,
        markAllAsRead,
        priorityConfig,
        priorityOptions,
        parseNotification,
        formatTimeAgo
    };
}
